import type { FC } from 'react'

import { Button } from '@skytech/ui/Button'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import CheckBoldIcon from 'mdi-react/CheckBoldIcon'
import CloseThickIcon from 'mdi-react/CloseThickIcon'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { useIsAdmin } from '@/hooks/access'
import queries from '@/modules/admin/queries'
import adminResources from '@/modules/admin/resources'
import type { Policy } from '@/resources/ecom-policy'
import { commonEcomPolicyResources } from '@/resources/ecom-policy'
import { commonQueries } from '@/resources/queries'

import type { CostObject, SalaryDeductionProfile } from '../../resources'

import './ConfirmationMessage.styles.scss'

const ConfirmationMessage: FC<{
  costObjects: CostObject[] | undefined
  affectedCostObjectsByEcomPolicy: {
    removedPolicyUsersByPolicyId: Record<number, number[]>
    assignedPolicyUsersByPolicyId: Record<number, number[]>
    numberOfAllAffectedCostObjects: number
  }
  affectedCostObjectsByTelePolicy: {
    removedPolicyUsersByPolicyId: Record<number, number[]>
    assignedPolicyUsersByPolicyId: Record<number, number[]>
    numberOfAllAffectedCostObjects: number
  }
  alteredEcomPolicyIds: {
    removed: number[]
    assigned: number[]
  }
  alteredTelePolicyIds: {
    removed: number[]
    assigned: number[]
  }
  onClose: () => void
  onCancel: () => void
}> = ({
  costObjects,
  affectedCostObjectsByEcomPolicy,
  alteredEcomPolicyIds,
  alteredTelePolicyIds,
  affectedCostObjectsByTelePolicy,
  onCancel,
  onClose,
}) => {
  const translate = useTranslation()
  const queryClient = useQueryClient()
  const isAdmin = useIsAdmin()

  const { data: availablePolicies } = useQuery(
    commonQueries.policyList(),
    commonEcomPolicyResources.getList
  )
  const { data: availableTelePolicies = [] } = useQuery(
    queries.adminTelePoliciesList(),
    adminResources.getTelePolicyList,
    {
      enabled: isAdmin,
    }
  )
  const confirmationMessages = getConfirmationMessage({
    ...affectedCostObjectsByEcomPolicy,
    policyList: availablePolicies || [],
    translate,
  })
  const confirmationMessagesTelePolicies = getConfirmationMessage({
    ...affectedCostObjectsByTelePolicy,
    policyList: availableTelePolicies || [],
    translate,
  })
  const costObjectIds = costObjects?.map(costObject => costObject.id)
  const assignPoliciesMutation = useMutation(adminResources.assignEcomPolicies)
  const assignTelePoliciesMutation = useMutation(adminResources.assignTelePolicies)

  async function handleAssignPolicies() {
    if (!costObjectIds) throw Error('No employees affected!')
    let mutations: Promise<null>[] = []

    if (affectedCostObjectsByEcomPolicy.numberOfAllAffectedCostObjects > 0)
      mutations.push(
        assignPoliciesMutation.mutateAsync({
          costObjectIds,
          addPolicyList: alteredEcomPolicyIds.assigned,
          removePolicyList: alteredEcomPolicyIds.removed,
        })
      )
    if (affectedCostObjectsByTelePolicy.numberOfAllAffectedCostObjects > 0) {
      mutations.push(
        assignTelePoliciesMutation.mutateAsync({
          costObjectIds,
          telePolicyId: alteredTelePolicyIds.assigned[0] || null,
        })
      )
    }

    await Promise.all(mutations)
    queryClient.invalidateQueries(queries.adminCostObjectList(), { refetchType: 'all' })
    costObjectIds?.forEach(id =>
      queryClient.invalidateQueries(queries.adminCostObjectDetails(id), {
        refetchType: 'all',
      })
    )
    onClose()
  }

  if (!costObjectIds) throw Error('No employees found!')

  const isPoliciesAssigning =
    assignPoliciesMutation.isLoading || assignTelePoliciesMutation.isLoading

  return (
    <div className="assign-policies-confirmation-message">
      {affectedCostObjectsByEcomPolicy.numberOfAllAffectedCostObjects > 0 && (
        <>
          <span>
            <Translation id="Are you sure that you want to assign/remove the ecom policies?" />
          </span>
          <div className="sub-messages-container">
            {confirmationMessages.map((message, index) => (
              <div key={message + '-' + index} className="sub-message">
                {message.type === 'assignment' ? (
                  <CheckBoldIcon className="list-icon assignment" />
                ) : (
                  <CloseThickIcon className="list-icon removal" />
                )}
                {message.content}
              </div>
            ))}
          </div>
        </>
      )}

      {affectedCostObjectsByTelePolicy.numberOfAllAffectedCostObjects > 0 && (
        <>
          <span>
            <Translation id="Are you sure that you want to assign/remove the tele policies?" />
          </span>

          <div className="sub-messages-container">
            {confirmationMessagesTelePolicies.map((message, index) => (
              <div key={message + '-' + index} className="sub-message">
                {message.type === 'assignment' ? (
                  <CheckBoldIcon className="list-icon assignment" />
                ) : (
                  <CloseThickIcon className="list-icon removal" />
                )}
                {message.content}
              </div>
            ))}
          </div>
        </>
      )}

      <footer>
        <Button variant="tertiary" onClick={onCancel}>
          <Translation id="Cancel" />
        </Button>

        <Button variant="primary" isLoading={isPoliciesAssigning} onClick={handleAssignPolicies}>
          <Translation id="Confirm" />
        </Button>
      </footer>
    </div>
  )
}

export default ConfirmationMessage

function getConfirmationMessage({
  removedPolicyUsersByPolicyId,
  assignedPolicyUsersByPolicyId,
  policyList,
  translate,
}: {
  removedPolicyUsersByPolicyId: Record<number, number[]>
  assignedPolicyUsersByPolicyId: Record<number, number[]>
  policyList: (Policy | SalaryDeductionProfile)[]
  translate: (id: string, values?: Record<string, string>) => string
}) {
  let messages: { type: 'removal' | 'assignment'; content: string }[] = []

  Object.entries(removedPolicyUsersByPolicyId).forEach(([policyId, costObjectIds]) => {
    const policyName = policyList.find(policy => policy.id === Number(policyId))?.name || ''
    const numberOfAffectedUsers = String(costObjectIds.length)
    messages.push({
      type: 'removal',
      content: translate(
        '{numberOfAffectedUsers} user(s) are being removed from the policy {policyName}.',
        {
          policyName,
          numberOfAffectedUsers,
        }
      ),
    })
  })

  Object.entries(assignedPolicyUsersByPolicyId).forEach(([policyId, costObjectIds]) => {
    const policyName = policyList.find(policy => policy.id === Number(policyId))?.name || ''
    const numberOfAffectedUsers = String(costObjectIds.length)
    messages.push({
      type: 'assignment',
      content: translate(
        '{numberOfAffectedUsers} user(s) are being assigned to the policy {policyName}.',
        {
          policyName,
          numberOfAffectedUsers,
        }
      ),
    })
  })

  return messages
}
